import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import VideoPlayer from '../components/VideoPlayer';
import { Zap, SkipForward, Volume2, Tv, Radio } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TVSchedulePage() {
  const [schedule, setSchedule] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tv-schedule`);
      setSchedule(data.schedule || []);
      setCurrentIndex(0);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const current = schedule[currentIndex];
  const next = schedule[currentIndex + 1];

  const channelSurf = () => {
    if (schedule.length <= 1) { fetchSchedule(); return; }
    const nextIdx = (currentIndex + 1) % schedule.length;
    setCurrentIndex(nextIdx);
  };

  const shuffleAll = () => { fetchSchedule(); };

  if (loading) return (
    <div className="page-content bg-[#0A0A0A] min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Tv size={48} className="text-[#FFDE00] mx-auto mb-4 animate-pulse" strokeWidth={1.5} />
        <p className="font-['Fredoka'] text-xl text-white">Tuning in...</p>
      </div>
    </div>
  );

  return (
    <div className="page-content bg-[#0A0A0A] min-h-screen" data-testid="tv-schedule-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FFDE00] border-2 border-[#FFDE00] flex items-center justify-center">
              <Zap size={20} strokeWidth={2.5} className="text-[#0A0A0A]" />
            </div>
            <div>
              <h1 className="font-['Fredoka'] text-2xl sm:text-3xl font-bold text-white">TV Mode</h1>
              <p className="text-xs text-white/50 font-['IBM_Plex_Mono'] uppercase tracking-wider">Channel Surf Experience</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF3860]/20 border border-[#FF3860]/40">
              <div className="w-2 h-2 rounded-full bg-[#FF3860] animate-pulse" />
              <span className="text-xs font-bold text-[#FF3860] font-['IBM_Plex_Mono']">LIVE</span>
            </div>
          </div>
        </div>

        {schedule.length === 0 ? (
          <div className="text-center py-20">
            <Radio size={48} className="text-white/30 mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-['Fredoka'] text-xl text-white/60 mb-4">No shows available yet</p>
            <p className="text-sm text-white/40 font-['Nunito']">Add shows via the admin panel to start the broadcast</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Player */}
            <div className="lg:col-span-2">
              {current && (
                <>
                  <VideoPlayer
                    videoId={current.type === 'episode' ? current.data.youtube_video_id : current.data.youtube_video_id}
                    title={current.data.title}
                    autoplay={true}
                    showScanlines={true}
                  />
                  {/* Now Playing Info */}
                  <div className="mt-4 p-4 rounded-xl border-2 border-white/10 bg-white/5" data-testid="now-playing-info">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 text-xs font-bold rounded border-2 ${
                        current.type === 'commercial'
                          ? 'bg-[#FFDE00] text-[#0A0A0A] border-[#FFDE00]'
                          : 'bg-[#FF3860] text-white border-[#FF3860]'
                      }`}>
                        {current.type === 'commercial' ? 'COMMERCIAL BREAK' : 'NOW PLAYING'}
                      </span>
                    </div>
                    <h2 className="font-['Fredoka'] text-xl font-bold text-white">
                      {current.data.title}
                    </h2>
                    {current.type === 'episode' && current.show && (
                      <p className="text-sm text-white/60 font-['Nunito'] mt-1">
                        {current.show.title} - S{current.data.season}E{current.data.episode_number}
                      </p>
                    )}
                    {current.type === 'commercial' && current.data.brand && (
                      <p className="text-sm text-white/60 font-['Nunito'] mt-1">{current.data.brand}</p>
                    )}
                  </div>

                  {/* Up Next Ticker */}
                  {next && (
                    <div
                      className="mt-3 flex items-center gap-3 rounded-xl border-2 border-[#FFDE00]/40 bg-black/60 overflow-hidden"
                      data-testid="up-next-ticker"
                    >
                      <div className="flex items-center gap-1.5 shrink-0 pl-3 pr-2 py-2 bg-[#FFDE00] text-[#0A0A0A]">
                        <div className="w-2 h-2 rounded-full bg-[#FF3860] animate-pulse" />
                        <span className="font-['IBM_Plex_Mono'] text-[11px] font-bold uppercase tracking-widest">
                          Up Next
                        </span>
                      </div>
                      <div className="flex-1 overflow-hidden whitespace-nowrap py-2 pr-3">
                        <div className="inline-block ticker-slide">
                          <span className="font-['IBM_Plex_Mono'] text-sm text-[#FFDE00] uppercase tracking-wide mr-2">
                            {next.type === 'commercial' ? 'AD BREAK ·' : 'SHOW ·'}
                          </span>
                          <span className="font-['Fredoka'] text-sm text-white font-bold">
                            {next.data.title}
                          </span>
                          {next.type === 'episode' && next.show && (
                            <span className="font-['Nunito'] text-sm text-white/60 ml-2">
                              — {next.show.title}
                            </span>
                          )}
                          <span className="font-['IBM_Plex_Mono'] text-sm text-white/30 mx-6">•••</span>
                          <span className="font-['IBM_Plex_Mono'] text-sm text-[#FFDE00] uppercase tracking-wide mr-2">
                            {next.type === 'commercial' ? 'AD BREAK ·' : 'SHOW ·'}
                          </span>
                          <span className="font-['Fredoka'] text-sm text-white font-bold">
                            {next.data.title}
                          </span>
                          {next.type === 'episode' && next.show && (
                            <span className="font-['Nunito'] text-sm text-white/60 ml-2">
                              — {next.show.title}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Controls */}
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={channelSurf}
                      className="neo-btn flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#FFDE00] text-[#0A0A0A] border-[#FFDE00]"
                      data-testid="channel-surf-btn"
                    >
                      <SkipForward size={18} strokeWidth={2.5} /> Channel Surf
                    </button>
                    <button
                      onClick={shuffleAll}
                      className="neo-btn flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 text-white border-white/20"
                      data-testid="shuffle-btn"
                    >
                      <Volume2 size={18} strokeWidth={2.5} /> New Schedule
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Schedule Sidebar */}
            <div className="lg:col-span-1">
              <h3 className="font-['Fredoka'] text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Radio size={16} strokeWidth={2.5} className="text-[#FFDE00]" /> What's On
              </h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                {schedule.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      i === currentIndex
                        ? 'border-[#FFDE00] bg-[#FFDE00]/10'
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                    data-testid={`schedule-item-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        i === currentIndex ? 'bg-[#FFDE00]' : 'bg-white/30'
                      }`} />
                      <span className={`text-xs font-bold font-['IBM_Plex_Mono'] uppercase ${
                        item.type === 'commercial' ? 'text-[#FFDE00]' : 'text-[#4AC6FF]'
                      }`}>
                        {item.type === 'commercial' ? 'AD' : 'SHOW'}
                      </span>
                    </div>
                    <p className={`text-sm font-['Nunito'] font-bold truncate ${
                      i === currentIndex ? 'text-white' : 'text-white/70'
                    }`}>
                      {item.data.title}
                    </p>
                    {item.type === 'episode' && item.show && (
                      <p className="text-xs text-white/40 font-['Nunito'] truncate">{item.show.title}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
